import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { CommentTreeNode } from '../../components/commentNew/commentHelpers';
import commonResource from '../../locales/th/common.json';
import { UserComment } from '../../queries/contentPage/commentList';
import { WholeState } from '../../store/common/type';
import { CommentAlert } from './CommentAlert';
import CommentForm from './CommentForm';
import {
  CommentAvatar,
  CommentDate,
  CommentItemBody,
  CommentItemWrapper,
  CommentMessage,
  CommentName,
  ReplyButton,
  RightCommentContent,
} from './CommentItem.style';
import { useCommentAlertMessage } from './useCommentMessage';
import { sanitize } from '../../common/helpers/utility/sanitizer';
import dateTimeHelper from '../../common/helpers/ui/DateTimeHelper';

interface CommentItemProps {
  comment: CommentTreeNode;
  postId: number;
  onNewCommentAdded: (comment: UserComment) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({
  postId,
  comment,
  onNewCommentAdded,
}) => {
  const { id } = comment;
  const [isShowForm, setIsShowForm] = useState(false);

  const { alertMessage, showAlertMessage } = useCommentAlertMessage();

  const userName = useSelector<WholeState, string>((state) =>
    state.common !== undefined ? state.common.displayName : '',
  );
  const email = useSelector<WholeState, string>((state) =>
    state.common !== undefined ? state.common.loginEmail : '',
  );

  return (
    <CommentItemWrapper>
      {alertMessage && <CommentAlert message={alertMessage} />}
      <CommentItemBody data-test={`comment-item-${comment.id}`}>
        <CommentAvatar />
        <RightCommentContent>
          <CommentName>{comment.name}</CommentName>
          <CommentDate>
            {dateTimeHelper.getCustomDateTime(comment.date)}
          </CommentDate>
          <CommentMessage
            dangerouslySetInnerHTML={{
              __html: sanitize(comment.message),
            }}
          />
          <ReplyButton
            role="button"
            data-test="reply-comment-button"
            onClick={() => setIsShowForm(true)}
          >
            {commonResource.comment.reply}
          </ReplyButton>
          {isShowForm && (
            <>
              <CommentForm
                profile={userName && email ? { userName, email } : null}
                onCancel={() => setIsShowForm(false)}
                onFinish={(comment) => {
                  setIsShowForm(false);
                  onNewCommentAdded(comment);
                }}
                onError={(errorCode) => showAlertMessage(errorCode)}
                parentCommentId={id}
                postId={postId}
              />
            </>
          )}
          {comment.children?.length > 0 &&
            comment.children.map((childComment) => (
              <CommentItem
                key={childComment.id}
                comment={childComment}
                postId={postId}
                onNewCommentAdded={onNewCommentAdded}
              />
            ))}
        </RightCommentContent>
      </CommentItemBody>
    </CommentItemWrapper>
  );
};

export default CommentItem;
